import type { CardType, GameState, PlayerId } from '@cards/engine';
import { getCardUi, getDeckDefinition, registry } from '@cards/cards';

export type PlannedCardKind = Extract<CardType, 'SPELL' | 'VIRUS' | 'REACTION'>;

export interface PlayerUiMechanics {
  readonly tracksTokenDeaths: boolean;
  readonly plannedCardKinds: readonly PlannedCardKind[];
}

const PLANNED_KIND_ORDER: readonly PlannedCardKind[] = ['SPELL', 'VIRUS', 'REACTION'];

function visibleDefinitionIds(state: GameState, playerId: PlayerId): string[] {
  return Object.values(state.instances)
    .filter((instance) => instance.ownerId === playerId && registry.has(instance.defId))
    .map((instance) => instance.defId);
}

/**
 * Derives only presentation capabilities. The selected prepared deck is the
 * preferred source because an opponent's unrevealed cards are redacted. Tests
 * and legacy rooms without that metadata fall back to public/owned cards.
 */
export function playerUiMechanics(
  state: GameState,
  playerId: PlayerId,
  deckDefinitionId?: string,
): PlayerUiMechanics {
  const deck = deckDefinitionId === undefined ? undefined : getDeckDefinition(deckDefinitionId);
  const definitionIds = deck?.cardIds ?? visibleDefinitionIds(state, playerId);
  const definitions = definitionIds.flatMap((id) => {
    const definition = registry.get(id);
    return definition === undefined ? [] : [definition];
  });
  const player = state.players[playerId];
  const tracksTokenDeaths = definitionIds.some((definitionId) =>
    getCardUi(definitionId)?.capabilities?.tracksTokenDeaths === true)
    || (deck === undefined && (player?.ordinaryTokenDeaths ?? 0) > 0);
  const kindSet = new Set(definitions.map((definition) => definition.type));

  return {
    tracksTokenDeaths,
    plannedCardKinds: PLANNED_KIND_ORDER.filter((kind) => kindSet.has(kind)),
  };
}
